import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import type { CognitoUser } from '@metron/shared';
import { createLogger, toErrorMessage } from '@metron/shared';

const logger = createLogger('PermanentDeleteUserDialog');

interface PermanentDeleteUserDialogProps {
  open: boolean;
  user: CognitoUser | null;
  onOpenChange: (open: boolean) => void;
  onPermanentDeleteUser: (userId: string) => Promise<void>;
}

export function PermanentDeleteUserDialog({
  open,
  user,
  onOpenChange,
  onPermanentDeleteUser,
}: PermanentDeleteUserDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');

  const confirmationPhrase = 'DELETE';
  const isConfirmed = confirmationText === confirmationPhrase;

  const handleDelete = async () => {
    if (!user || !isConfirmed) return;

    setIsLoading(true);
    try {
      await onPermanentDeleteUser(user.username);
      toast.success('User permanently deleted');
      setConfirmationText('');
      onOpenChange(false);
    } catch (error) {
      const errorMessage = toErrorMessage(error, 'Failed to delete user');
      toast.error(errorMessage);
      logger.error({ error }, 'Failed to permanently delete user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setConfirmationText('');
    }
    onOpenChange(newOpen);
  };

  if (!user) return null;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className='max-w-md'>
        <AlertDialogHeader>
          <div className='flex items-center gap-3 mb-2'>
            <div className='flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10'>
              <AlertTriangle className='h-5 w-5 text-destructive' />
            </div>
            <AlertDialogTitle className='text-destructive'>
              Permanently Delete User
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className='space-y-4 text-left'>
              <div className='rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm'>
                <p className='font-semibold text-destructive mb-1'>
                  Warning: This action cannot be undone!
                </p>
                <p className='text-muted-foreground'>
                  This will permanently delete the user account and all associated data from the
                  system. The user will need to be recreated from scratch if access is needed again.
                </p>
              </div>

              <div className='space-y-1 text-sm'>
                <p>
                  <span className='text-muted-foreground'>User:</span>{' '}
                  <span className='font-medium'>{user.name}</span>
                </p>
                <p>
                  <span className='text-muted-foreground'>Email:</span>{' '}
                  <span className='font-medium'>{user.email}</span>
                </p>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='confirmation' className='text-sm font-medium'>
                  Type{' '}
                  <span className='font-mono font-bold text-destructive'>{confirmationPhrase}</span>
                  {' '}
                  to confirm
                </Label>
                <Input
                  id='confirmation'
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder={confirmationPhrase}
                  className='font-mono'
                  disabled={isLoading}
                />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading} onClick={() => setConfirmationText('')}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isLoading || !isConfirmed}
            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
          >
            {isLoading ? 'Deleting...' : 'Permanently Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
